import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { ActionButtonVariants } from 'components/action-button';
import { Modal, ModalSize } from 'components/modal';
import { useFeatureInvitationCode } from 'hooks/use-feature-invitation-code';
import { IUnlockedIncentiveModalStatus } from 'state/loyalty/hooks/use-loyalty-quests';

import {
  CTAButton,
  CloseButton,
  Container,
  ImageWrapper,
  MainTitle,
  OfferDescription,
  OfferImage,
  TextContentWrapper,
} from './modal-quest-unlocked-incentive.styled';

interface ModalQuestUnlockedIncentiveProps {
  onChangeUnlockedIncentivesModalVisibilityStatus: (
    unlockedIncentivesModalVisibilityStatus: IUnlockedIncentiveModalStatus
  ) => void;
  unlockedIncentivesModalVisibilityStatus?: IUnlockedIncentiveModalStatus;
}

const ModalContent = ({
  onChangeUnlockedIncentivesModalVisibilityStatus,
}: ModalQuestUnlockedIncentiveProps) => {
  const { featureInvitationCode } = useFeatureInvitationCode();
  const { formatMessage } = useIntl();

  const navigate = useNavigate();

  const { featureInvitationCodeLoading } = useFeatureInvitationCode();

  const invitationCodeBenefit =
    featureInvitationCode?.invitationCodeBenefit && featureInvitationCode?.invitationCodeBenefit[0];

  const onCTAClickHandler = useCallback(() => {
    const navigationURL =
      featureInvitationCode?.invitationCodeUnlocksBenefitModal?.shareMyCodeButtonLink?.locale;
    if (!navigationURL) {
      return;
    }

    onChangeUnlockedIncentivesModalVisibilityStatus({
      showUnlockedOfferModalStatus: false,
    });

    navigate(navigationURL);
  }, [featureInvitationCode, navigate, onChangeUnlockedIncentivesModalVisibilityStatus]);

  return (
    <Container>
      <ImageWrapper>
        <OfferImage image={invitationCodeBenefit?.image?.locale} alt="" />
      </ImageWrapper>
      <TextContentWrapper>
        <MainTitle>
          {featureInvitationCode?.invitationCodeUnlocksBenefitModal?.title?.locale}
        </MainTitle>
        <OfferDescription>
          {' '}
          {featureInvitationCode?.invitationCodeUnlocksBenefitModal?.description?.locale}{' '}
        </OfferDescription>
        <CTAButton
          disabled={featureInvitationCodeLoading}
          onClick={onCTAClickHandler}
          aria-label="quest-unlocked-incentive"
          data-testid="quest-unlocked-incentive-button"
        >
          {featureInvitationCode?.invitationCodeUnlocksBenefitModal?.shareMyCodeButtonText?.locale}
        </CTAButton>
        <CloseButton
          disabled={featureInvitationCodeLoading}
          onClick={() =>
            onChangeUnlockedIncentivesModalVisibilityStatus({
              showUnlockedOfferModalStatus: false,
            })
          }
          aria-label="quest-unlocked-incentive-close"
          data-testid="quest-unlocked-incentive-close-button"
          variant={ActionButtonVariants.TEXT_ONLY}
        >
          {formatMessage({ id: 'close' })}
        </CloseButton>
      </TextContentWrapper>
    </Container>
  );
};

export const ModalQuestUnlockedIncentive = ({
  onChangeUnlockedIncentivesModalVisibilityStatus,
  unlockedIncentivesModalVisibilityStatus,
}: ModalQuestUnlockedIncentiveProps) => {
  if (!unlockedIncentivesModalVisibilityStatus?.showUnlockedOfferModalStatus) {
    return null;
  }

  return (
    <Modal
      data-testid="quest-unlocked-modal-incentive"
      backgroundColor="transparent"
      allowsDismiss={false}
      eventData={{
        modalAppearanceEventMessage: 'Modal Quest Completed - Unlocked Benefit',
        modalMessage: 'Modal Quest Completed - Unlocked Benefit',
      }}
      onDismiss={() =>
        onChangeUnlockedIncentivesModalVisibilityStatus({
          showUnlockedOfferModalStatus: false,
        })
      }
      size={ModalSize.AUTO}
      overrideStyles={{
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <ModalContent
        onChangeUnlockedIncentivesModalVisibilityStatus={
          onChangeUnlockedIncentivesModalVisibilityStatus
        }
      />
    </Modal>
  );
};
